import { visuallyHidden } from '@mui/utils';
import TableSortLabel from '@mui/material/TableSortLabel';
import MuiTableHead from '@mui/material/TableHead';
import TableCell from './TableCell';
import TableRow from '@mui/material/TableRow';
import TableCheckbox from './TableCheckbox';
import Box from '@mui/material/Box';
import { ColumnDef, Order } from './TableHelpers';

const TableHead = <T extends { id: number }>({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  columns,
  onRequestSort,
  selectionEnabled,
  dense = false,
}: {
  numSelected: number;
  onRequestSort: (property: keyof T) => void;
  onSelectAllClick: (checked: boolean) => void;
  order: Order;
  orderBy: keyof T | null;
  rowCount: number;
  columns: Array<ColumnDef<T>>;
  selectionEnabled?: boolean;
  dense?: boolean;
}) => {
  const handleSortClick = (column: ColumnDef<T>) => {
    if (column.field && column.sortable !== false) {
      onRequestSort(column.field);
    }
  };

  return (
    <MuiTableHead>
      <TableRow>
        {selectionEnabled ? (
          <TableCell head dense={dense}>
            <TableCheckbox
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
            />
          </TableCell>
        ) : null}
        {columns.map((column) => {
          const key =
            column.field?.toString() || column.id || 'action';
          const cellSortDirection =
            column.field && orderBy === column.field ? order : false;
          const sortActive = column.field && orderBy === column.field;
          const labelSortDirection =
            column.field && orderBy === column.field ? order : 'asc';
          const isSortable =
            column.field && column.sortable !== false;

          return (
            <TableCell
              key={key}
              align={column.align || 'left'}
              padding={column.align === 'right' ? 'none' : 'normal'}
              sortDirection={cellSortDirection}
              head
              dense={dense}
              style={{ width: column.width, height: dense ? 40 : 60 }}
            >
              <TableSortLabel
                active={sortActive}
                direction={labelSortDirection}
                onClick={() => handleSortClick(column)}
                hideSortIcon={!isSortable}
                disabled={!isSortable}
              >
                {column.label}
                {column.field && orderBy === column.field ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </MuiTableHead>
  );
};

export default TableHead;
